import queryString from 'query-string';
import {FC, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';

import {fetchScales} from '~src/modules/scales/actions/scaleActions';
import {fetchTopicsList, FetchTopicsParams, TopicSortParam} from '~src/modules/topics/actions/topicActions';
import {getTopics} from '~src/modules/topics/reducers/topicsReducer';
import {StoreI} from '~src/rootReducer';
import {routes} from '~src/routingConfig';
import {isError, ThunkMWDispatch} from '~src/services/apiMiddleware';
import TextBtnPageFooter from '~src/shared/footers/textBtnPageFooter/TextBtnPageFooter';
import ListPageHeader from '~src/shared/headers/listPageHeader/ListPageHeader';
import ItemsAmount from '~src/shared/lists/itemsAmount/ItemsAmount';
import ListAddButton from '~src/shared/lists/listAddButton/ListAddButton';
import Pagination from '~src/shared/lists/pagination/Pagination';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import {MainContainerAnimation} from '~src/styles/animations';
import {ListMargin, PageContentContainer} from '~src/styles/containers.sc';

import {TopicSubject} from '~src/interfaces/topic.interface';
import {topicTypeFilterOpts} from '~src/modules/reports/shared/constants/filterOpt';
import {topicsAndQuestionsSortingOptions} from '~src/modules/reports/shared/constants/sortingOptions';
import {translateContent} from '~src/services/translationServices';
import ListFilters, {FilterI} from '~src/shared/lists/listFilters/ListFilters';
import SortSelector from '~src/shared/sortSelector/SortSelector';
import TopicItem from './TopicItem';
import {ItemsAmountRow, TopicsContainer} from './topicsList.sc';
import {getCatchSubmitAnyError} from "~src/shared/reactFinalForm/catchSubmitAnyError";
import {useHistory, useLocation} from "react-router";
import {SortingValues} from "~src/modules/reports/shared/constants/filterAndSortingKeys";

const namedFilters = [TopicSubject.All, TopicSubject.People, TopicSubject.General, TopicSubject.Groups];

interface TopicsListSearchParams {
  page: string;
  per_page: string;
  'filter.archived': boolStr;
  'filter.subject': string;
  sort: string;
}

type boolStr = 'true' | 'false';

function strToOptBool(str?: string): boolean|undefined {
  return str === 'true' ? true : (str === 'false' ? false : undefined);
}

function boolToOptStr(bool?: boolean): boolStr|undefined {
  return bool === true ? 'true' : (bool === false ? 'false' : undefined);
}

export const TopicsList: FC = () => {

  const { t } = useTranslation();
  const topics = useSelector((s: StoreI) => getTopics(s));
  const scales = useSelector((s: StoreI) => s.scales.byId);
  const loadingTopics = useSelector((s: StoreI) => s.topics.loading);
  const loadingScales = useSelector((s: StoreI) => s.scales.loadingScales);
  const pagination = useSelector((s: StoreI) =>s.topics.pagination);
  const location = useLocation();
  const history = useHistory();

  const setTopicListSearchParam = (params: Partial<TopicsListSearchParams>): void => {
    const url = new URL(window.location.href);
    Object.entries(params).forEach(([key, val]) => url.searchParams.set(key, val));
    history.push({search: url.search});
  };

  const removeTopicListSearchParam = (key: keyof TopicsListSearchParams): void => {
    const url = new URL(window.location.href);
    url.searchParams.delete(key);
    history.push({search: url.search});
  }

  const fetchTopicParams: FetchTopicsParams = useMemo(() => {
    const searchParams: Partial<TopicsListSearchParams> = queryString.parse(location.search);

    return {
      page: parseInt(searchParams.page || '1', 10) || 1,
      sort: (searchParams.sort as TopicSortParam),
      "filter.archived": strToOptBool(searchParams["filter.archived"]),
      "filter.subject": (searchParams["filter.subject"] as TopicSubject) || TopicSubject.All,
    };
  }, [location.search]);

  const dispatch = useDispatch<ThunkMWDispatch>();

  useEffect(() => {
    dispatch(fetchScales({ per_page: 1000 })).then((res) => {
      if (isError(res)) {
        throw new Error('Error on fetching scales list');
      }}).catch(getCatchSubmitAnyError('TopicsList.tsx', 'error fetching scales'));
  }, [dispatch]);


  useEffect(() => {
    dispatch(fetchTopicsList(fetchTopicParams)).then((res) => {
      if (isError(res))
        throw new Error('Error on fetching topics list');
    }).catch(getCatchSubmitAnyError('TopicsList.tsx', 'error fetching topics'));
  }, [dispatch, fetchTopicParams]);

  const goToAdd = () => {
    history.push(routes.contentTopics.topicsAdd);
  };

  const filterOptions: FilterI[] = topicTypeFilterOpts.map((filter, i) => {
    let itemsAmount;
    let tooltipKey;
    const filterId = filter.id as TopicSubject;
    if (i !== 0) {
      tooltipKey = `x.topicAndQuestionLibrary.filter.hover.${filterId}`;
      itemsAmount = topics.reduce(
        (accum, topic) => accum + (topic.subject === filterId ? 1 : 0),
        0,
      ) || undefined;  // take care to NOT display a "0" if we dont actually know the number
    }

    return {
      ...filter,
      itemsAmount,
      disabled: false,
      tooltipKey,
    };
  });

  const sortList = (sortValue: SortingValues) => {
    setTopicListSearchParam({ sort: sortValue as TopicSortParam});
  };
  const filteredTopics = topics
    .filter((topic) => {
      if (!fetchTopicParams["filter.subject"]) return true;

      const subject = topic.subject;
      return subject === fetchTopicParams["filter.subject"];
    })
    .sort((t1, t2) => {
      switch (fetchTopicParams.sort) {
        case SortingValues.TopicNames_asc:
          return translateContent(t1.name) < translateContent(t2.name) ? -1 : 1;
        case SortingValues.TopicNames_desc:
          return translateContent(t1.name) < translateContent(t2.name) ? 1 : -1;
        case SortingValues.Created_asc:
          return new Date(t1.createdAt).getTime() - new Date(t2.createdAt).getTime();
        case SortingValues.Created_desc:
          return new Date(t2.createdAt).getTime() - new Date(t1.createdAt).getTime();
      }
      return 0;
    });

  const deactivatedFilterOptions: FilterI[] = [
    {
      id: 'undefined',
      labelKey: 'x.topics.filter.archived.undefined',
    },
    {
      id: 'true',
      labelKey: 'x.topics.filter.archived.true'
    },
    {
      id: 'false',
      labelKey: 'x.topics.filter.archived.false'
    },
  ]

  return (
    <main>
      <ListPageHeader
        t={t}
        titleTxt="x.topics.list.header.title"
        subTitleTxt="x.topics.list.header.subtitle"
      />
      <PageContentContainer>
        <ListMargin>
          <ListFilters
            titleKey={'x.topics.list.filters.subject_title'}
            filterOptions={filterOptions}
            initialFilter={fetchTopicParams["filter.subject"]}
            doFilter={(filterIndex) => {
              const activeFilter = namedFilters[filterIndex];
              if (activeFilter === TopicSubject.All) {
                removeTopicListSearchParam('filter.subject');
              } else {
                setTopicListSearchParam({'filter.subject': activeFilter});
              }
            }}
          />
          <ListFilters
            titleKey={'x.topics.list.filters.filter_archived'}
            filterOptions={deactivatedFilterOptions}
            initialFilter={boolToOptStr(fetchTopicParams["filter.archived"])}
            doFilter={(filterIndex) => {
              const activeFilter = deactivatedFilterOptions[filterIndex];
              const v = boolToOptStr(strToOptBool(activeFilter.id));
              if (v === undefined) {
                removeTopicListSearchParam('filter.archived');
              } else {
                setTopicListSearchParam({'filter.archived':  v});
              }
            }
          }
          />
          <ItemsAmountRow>
            {pagination && !loadingTopics && (
              <ItemsAmount
                pagination={pagination}
                onePageKey="x.topics.list.topics_amount"
                multyPageKey="x.topics.list.topics_amount_from_to"
              />
            )}
            <SortSelector
              sortingOptions={topicsAndQuestionsSortingOptions()}
              selectedSorting={fetchTopicParams.sort || SortingValues.Created_desc}
              sortList={sortList}
              selectorWithLabel
            />
          </ItemsAmountRow>
          {loadingTopics || loadingScales ? (
            <LoadingSpinner marginTop={60} />
          ) : (
            <MainContainerAnimation>
              <ListAddButton
                addFunc={goToAdd}
                buttonText={{
                  key: 'x.topics.list.add.button',
                  txt: t('x.topics.list.add.button'),
                }}
              />
              <TopicsContainer>
                {filteredTopics.map(topic => (
                  <TopicItem
                    topic={topic}
                    key={topic.id}
                    scale={scales[topic.scaleId]}
                    link={routes.contentTopics.topic(topic.id)}
                    locationState={{ fromTopicsListings: true }}
                  />
                ))}
              </TopicsContainer>
            </MainContainerAnimation>
          )}
          {pagination && (
            <Pagination
              t={t}
              totalItems={pagination.total}
              pageSize={pagination.per_page}
              onChange={page => setTopicListSearchParam({page: String(page)})}
              marginTop={60}
              history={history}
              location={location}
              defaultCurrPage={fetchTopicParams.page || 1}
            />
          )}
          <TextBtnPageFooter
            t={t}
            infoKey="x.topics.list.footer.title"
            btnKey="x.topics.list.footer.button"
            linkUrl={routes.contentSurveys.surveys}
          />
        </ListMargin>
      </PageContentContainer>
    </main>
  );
};

export default TopicsList;
